import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import CookieConsent from "@/components/CookieConsent";
import Chatwoot from "@/components/Chatwoot";
import HomePage from "@/pages/HomePage";
import SolutionsPage from "@/pages/SolutionsPage";
import AudiencePage from "@/pages/AudiencePage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import SegmentPage from "@/pages/SegmentPage";
import { SEGMENTS } from "@/lib/segments";
import { initAnalytics, trackPageView, captureUtmParams } from "@/lib/tracking";

function ScrollManager() {
    const { pathname, hash, key } = useLocation();
    useEffect(() => {
        window.__lenis?.scrollTo(0, { immediate: true });
        trackPageView(pathname);
    }, [pathname]);
    useEffect(() => {
        if (!hash) return undefined;
        // Wait a frame so the target page has rendered before looking up the anchor.
        const timer = setTimeout(() => {
            const target = document.getElementById(decodeURIComponent(hash.slice(1)));
            if (!target) return;
            if (window.__lenis) window.__lenis.scrollTo(target, { offset: -96 });
            else target.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 80);
        return () => clearTimeout(timer);
    }, [pathname, hash, key]);
    return null;
}

function AppLayout() {
    return (
        <>
            <ScrollManager />
            <Header />
            <main>
                <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/solucoes" element={<SolutionsPage />} />
                    <Route path="/para-quem" element={<AudiencePage />} />
                    <Route path="/sobre" element={<AboutPage />} />
                    <Route path="/contato" element={<ContactPage />} />
                    <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
                    {Object.values(SEGMENTS).map((segment) => (
                        <Route
                            key={segment.slug}
                            path={`/${segment.slug}`}
                            element={<SegmentPage config={segment} />}
                        />
                    ))}
                    <Route path="*" element={<HomePage />} />
                </Routes>
            </main>
            <Footer />
            <FloatingWhatsApp />
            <CookieConsent />
            <Chatwoot />
            <Toaster position="top-center" richColors />
        </>
    );
}

export default function App() {
    useEffect(() => {
        captureUtmParams();
        initAnalytics();
        const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduceMotion) return undefined;
        const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
        window.__lenis = lenis;
        let rafId;
        const loop = (time) => {
            lenis.raf(time);
            rafId = requestAnimationFrame(loop);
        };
        rafId = requestAnimationFrame(loop);
        return () => {
            cancelAnimationFrame(rafId);
            lenis.destroy();
        };
    }, []);

    return (
        <BrowserRouter>
            <AppLayout />
        </BrowserRouter>
    );
}
